// Timing a frame's own GPU work, shared by ?bench (js/bench.js) and the
// automatic glass's trial (js/glass/auto.js).
//
// A frame is timed with the GPU drained before it and waited for after it,
// so the number is the frame's own work and not the display's pace. The
// wait is a 1-pixel readPixels from the canvas, not gl.finish(): readPixels
// has to hand back the finished pixels, so no engine can return before every
// earlier draw has run, whereas finish() is only a request, which engines
// with a separate GPU process may satisfy early. (perf/first-visit.mjs and
// perf/sweep.mjs time the same way, so the numbers line up with the Mac's.)

const sortedOf = (xs) => [...xs].sort((a, b) => a - b);

/** The median of `xs` (the mean of the middle two for an even count). */
export function median(xs) {
  if (!xs.length) throw new RangeError("median of no values");
  const s = sortedOf(xs);
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** The q-th quantile (0..1) of `xs` by nearest rank: a value of `xs`. */
export function quantile(xs, q) {
  if (!xs.length) throw new RangeError("quantile of no values");
  if (!(q >= 0 && q <= 1)) throw new RangeError(`quantile must be in 0..1, got ${q}`);
  const s = sortedOf(xs);
  return s[Math.max(0, Math.ceil(q * s.length) - 1)];
}

/** The GPU wait for `renderer` (three's WebGLRenderer): a function that
 * returns once every draw issued before it has run. Throws (and so does the
 * wait) on a GL error. */
export function createGpuSync(renderer) {
  const gl = renderer.getContext();
  const px = new Uint8Array(4);
  const sync = () => {
    // the canvas's own framebuffer: a frame ends there
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
    const err = gl.getError();
    if (err !== gl.NO_ERROR) throw new Error(`readPixels failed: GL error 0x${err.toString(16)}`);
  };
  sync();
  return sync;
}

/** The mean ms of `frames` calls of `draw`, each drained and waited for on
 * its own (`sync`, createGpuSync's), timed together. */
export function timeFrames(sync, draw, frames = 1) {
  if (!(Number.isInteger(frames) && frames >= 1)) throw new RangeError(`frames must be a whole number >= 1, got ${frames}`);
  sync();
  const t0 = performance.now();
  for (let i = 0; i < frames; i++) {
    draw();
    sync();
  }
  return (performance.now() - t0) / frames;
}
